import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowLeft,
  FiSave,
  FiMail,
  FiDatabase,
  FiKey,
  FiGlobe,
  FiEye,
  FiEyeOff,
  FiCheck,
  FiUsers
} from 'react-icons/fi';
import superadminService from '../../../services/superadminService';
import { Tenant, TenantConfig, SMTPConfig, StorageConfig } from '../../../types/tenant';
import { useNotification } from '../../../contexts/NotificationContext';
import TenantUsers from './TenantUsers';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 2rem;
    color: #333;
    margin: 0;
  }
  
  .tenant-name {
    font-size: 1rem;
    color: #666;
    font-weight: normal;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
  
  svg {
    font-size: 1.25rem;
    color: #666;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #f0f0f0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#ff6b6b' : 'transparent'};
  color: ${props => props.$active ? '#ff6b6b' : '#666'};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: -2px;
  
  &:hover {
    color: #ff6b6b;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  color: #333;
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  
  &:hover {
    color: #333;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  
  input {
    cursor: pointer;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background-color: #ff6b6b;
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: #ff5252;
  }
`;

const TestButton = styled(Button)`
  background: white;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
  
  &:hover:not(:disabled) {
    background-color: #fee;
  }
`;

const ApiKeyItem = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  input:first-child {
    flex: 0 0 200px;
  }
  
  input:last-child {
    flex: 1;
  }
`;

const AddButton = styled.button`
  color: #ff6b6b;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

type TabType = 'smtp' | 'storage' | 'api' | 'security' | 'users';

const TenantConfigPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('smtp');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  
  const [config, setConfig] = useState<TenantConfig>({
    smtp_config: {
      host: '',
      port: 587,
      username: '',
      password: '',
      use_tls: true,
      from_email: '',
      from_name: ''
    },
    storage_config: {
      type: 'local',
      local_path: '/app/uploads',
      s3_bucket: '',
      s3_region: 'us-east-1',
      s3_access_key: '',
      s3_secret_key: '',
      s3_endpoint: '',
      max_file_size: 10485760,
      allowed_extensions: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx']
    },
    api_keys: {},
    security_config: {},
    allowed_origins: []
  });
  
  const [apiKeys, setApiKeys] = useState<Array<{ key: string; value: string }>>([]);

  useEffect(() => {
    loadTenant();
  }, [tenantId]);

  const loadTenant = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      const data = await superadminService.getTenantConfig(tenantId);
      setTenant(data);
      
      // Load configurations
      setConfig({
        smtp_config: data.smtp_config || config.smtp_config,
        storage_config: data.storage_config || config.storage_config,
        api_keys: data.api_keys || {},
        security_config: data.security_config || {},
        allowed_origins: data.allowed_origins || []
      });
      
      // Convert api_keys to array for editing
      if (data.api_keys) {
        setApiKeys(Object.entries(data.api_keys).map(([key, value]) => ({ key, value: String(value) })));
      }
    } catch (error) {
      showNotification('Erro ao carregar tenant', 'error');
      navigate('/superadmin/tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (field: keyof SMTPConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      smtp_config: {
        ...prev.smtp_config!,
        [field]: value
      }
    }));
  };

  const handleStorageChange = (field: keyof StorageConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      storage_config: {
        ...prev.storage_config!,
        [field]: value
      }
    }));
  };

  const handleApiKeyChange = (index: number, field: 'key' | 'value', value: string) => {
    const newApiKeys = [...apiKeys];
    newApiKeys[index] = { ...newApiKeys[index], [field]: value };
    setApiKeys(newApiKeys);
  };

  const addApiKey = () => {
    setApiKeys([...apiKeys, { key: '', value: '' }]);
  };

  const removeApiKey = (index: number) => {
    setApiKeys(apiKeys.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    
    try {
      setSaving(true);
      
      // Convert apiKeys array back to object
      const apiKeysObj = apiKeys.reduce((acc, { key, value }) => {
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string>);
      
      const updatedConfig = {
        ...config,
        api_keys: apiKeysObj
      };
      
      await superadminService.updateTenantConfig(tenantId, updatedConfig);
      showNotification('Configurações salvas com sucesso', 'success');
    } catch (error) {
      showNotification('Erro ao salvar configurações', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!tenantId || !config.smtp_config?.from_email) {
      showNotification('Configure o email de envio primeiro', 'error');
      return;
    }
    
    try {
      await superadminService.testSmtpConfig(tenantId, config.smtp_config.from_email);
      showNotification('Configuração SMTP válida', 'success');
    } catch (error) {
      showNotification('Erro na configuração SMTP', 'error');
    }
  };

  const togglePassword = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <Container>
        <p>Carregando configurações...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/superadmin/tenants')}>
          <FiArrowLeft />
        </BackButton>
        <h2>
          Configurações do Tenant
          {tenant && <span className="tenant-name"> - {tenant.name}</span>}
        </h2>
      </Header>

      <Tabs>
        <Tab $active={activeTab === 'smtp'} onClick={() => setActiveTab('smtp')}>
          <FiMail /> Email (SMTP)
        </Tab>
        <Tab $active={activeTab === 'storage'} onClick={() => setActiveTab('storage')}>
          <FiDatabase /> Armazenamento
        </Tab>
        <Tab $active={activeTab === 'api'} onClick={() => setActiveTab('api')}>
          <FiKey /> API Keys
        </Tab>
        <Tab $active={activeTab === 'security'} onClick={() => setActiveTab('security')}>
          <FiGlobe /> Segurança
        </Tab>
        <Tab $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          <FiUsers /> Usuários
        </Tab>
      </Tabs>

      {activeTab === 'smtp' && (
        <Card>
          <CardTitle>Configuração de Email (SMTP)</CardTitle>
          
          <FormGroup>
            <Label>Servidor SMTP</Label>
            <Input
              type="text"
              value={config.smtp_config?.host || ''}
              onChange={(e) => handleSmtpChange('host', e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Porta</Label>
            <Input
              type="number"
              value={config.smtp_config?.port || 587}
              onChange={(e) => handleSmtpChange('port', parseInt(e.target.value))}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Usuário</Label>
            <Input
              type="text"
              value={config.smtp_config?.username || ''}
              onChange={(e) => handleSmtpChange('username', e.target.value)}
              placeholder="email@empresa.com.br"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Senha</Label>
            <InputGroup>
              <Input
                type={showPasswords['smtp_password'] ? 'text' : 'password'}
                value={config.smtp_config?.password || ''}
                onChange={(e) => handleSmtpChange('password', e.target.value)}
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePassword('smtp_password')}
              >
                {showPasswords['smtp_password'] ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputGroup>
          </FormGroup>
          
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                checked={config.smtp_config?.use_tls || false}
                onChange={(e) => handleSmtpChange('use_tls', e.target.checked)}
              />
              Usar TLS
            </Checkbox>
          </FormGroup>
          
          <FormGroup>
            <Label>Email de Envio</Label>
            <Input
              type="email"
              value={config.smtp_config?.from_email || ''}
              onChange={(e) => handleSmtpChange('from_email', e.target.value)}
              placeholder="noreply@empresa.com.br"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Nome do Remetente</Label>
            <Input
              type="text"
              value={config.smtp_config?.from_name || ''}
              onChange={(e) => handleSmtpChange('from_name', e.target.value)}
              placeholder={tenant?.name || 'Nome da Empresa'}
            />
          </FormGroup>
        </Card>
      )}

      {activeTab === 'storage' && (
        <Card>
          <CardTitle>Configuração de Armazenamento</CardTitle>
          
          <FormGroup>
            <Label>Tipo de Armazenamento</Label>
            <Select
              value={config.storage_config?.type || 'local'}
              onChange={(e) => handleStorageChange('type', e.target.value)}
            >
              <option value="local">Local</option>
              <option value="s3">Amazon S3 / Compatível</option>
            </Select>
          </FormGroup>
          
          {config.storage_config?.type === 'local' && (
            <FormGroup>
              <Label>Caminho Local</Label>
              <Input
                type="text"
                value={config.storage_config?.local_path || ''}
                onChange={(e) => handleStorageChange('local_path', e.target.value)}
                placeholder="/app/uploads"
              />
            </FormGroup>
          )}
          
          {config.storage_config?.type === 's3' && (
            <>
              <FormGroup>
                <Label>Bucket S3</Label>
                <Input
                  type="text"
                  value={config.storage_config?.s3_bucket || ''}
                  onChange={(e) => handleStorageChange('s3_bucket', e.target.value)}
                  placeholder="meu-bucket"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Região</Label>
                <Input
                  type="text"
                  value={config.storage_config?.s3_region || ''}
                  onChange={(e) => handleStorageChange('s3_region', e.target.value)}
                  placeholder="us-east-1"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Access Key</Label>
                <Input
                  type="text"
                  value={config.storage_config?.s3_access_key || ''}
                  onChange={(e) => handleStorageChange('s3_access_key', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Secret Key</Label>
                <InputGroup>
                  <Input
                    type={showPasswords['s3_secret'] ? 'text' : 'password'}
                    value={config.storage_config?.s3_secret_key || ''}
                    onChange={(e) => handleStorageChange('s3_secret_key', e.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword('s3_secret')}
                  >
                    {showPasswords['s3_secret'] ? <FiEyeOff /> : <FiEye />}
                  </PasswordToggle>
                </InputGroup>
              </FormGroup>
              
              <FormGroup>
                <Label>Endpoint (opcional)</Label>
                <Input
                  type="text"
                  value={config.storage_config?.s3_endpoint || ''}
                  onChange={(e) => handleStorageChange('s3_endpoint', e.target.value)}
                  placeholder="https://s3.amazonaws.com"
                />
                <HelpText>
                  Deixe vazio para usar o endpoint padrão da AWS
                </HelpText>
              </FormGroup>
            </>
          )}
          
          <FormGroup>
            <Label>Tamanho Máximo de Arquivo (bytes)</Label>
            <Input
              type="number"
              value={config.storage_config?.max_file_size || 10485760}
              onChange={(e) => handleStorageChange('max_file_size', parseInt(e.target.value))}
            />
            <HelpText>
              Padrão: 10MB (10485760 bytes)
            </HelpText>
          </FormGroup>
        </Card>
      )}

      {activeTab === 'api' && (
        <Card>
          <CardTitle>Chaves de API</CardTitle>
          
          {apiKeys.map((apiKey, index) => (
            <ApiKeyItem key={index}>
              <Input
                type="text"
                value={apiKey.key}
                onChange={(e) => handleApiKeyChange(index, 'key', e.target.value)}
                placeholder="GOOGLE_MAPS_KEY"
              />
              <InputGroup>
                <Input
                  type={showPasswords[`api_${index}`] ? 'text' : 'password'}
                  value={apiKey.value}
                  onChange={(e) => handleApiKeyChange(index, 'value', e.target.value)}
                  placeholder="Valor da chave"
                />
                <PasswordToggle
                  type="button"
                  onClick={() => togglePassword(`api_${index}`)}
                >
                  {showPasswords[`api_${index}`] ? <FiEyeOff /> : <FiEye />}
                </PasswordToggle>
              </InputGroup>
              <Button type="button" onClick={() => removeApiKey(index)}>
                Remover
              </Button>
            </ApiKeyItem>
          ))}
          
          <AddButton type="button" onClick={addApiKey}>
            + Adicionar Chave de API
          </AddButton>
        </Card>
      )}

      {activeTab === 'security' && (
        <Card>
          <CardTitle>Configurações de Segurança</CardTitle>
          
          <FormGroup>
            <Label>Origens Permitidas (CORS)</Label>
            <TextArea
              value={config.allowed_origins?.join('\n') || ''}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                allowed_origins: e.target.value.split('\n').filter(Boolean)
              }))}
              placeholder="https://app.exemplo.com\nhttps://www.exemplo.com"
            />
            <HelpText>
              Uma origem por linha. Necessário para permitir requisições de domínios específicos.
            </HelpText>
          </FormGroup>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card>
          <TenantUsers 
            tenantId={tenantId || ''} 
            tenantName={tenant?.name}
          />
        </Card>
      )}

      <Actions>
        {activeTab === 'smtp' && (
          <TestButton type="button" onClick={handleTestSmtp}>
            <FiCheck />
            Testar SMTP
          </TestButton>
        )}
        
        <SaveButton onClick={handleSave} disabled={saving}>
          <FiSave />
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </SaveButton>
      </Actions>
    </Container>
  );
};

export default TenantConfigPage;